import { isAxiosError } from 'axios'
import { markRaw } from 'vue'
import * as Sentry from '@sentry/vue'
import { callApi, buildSignedNestStreamRequest } from '@/store/apiUtils'
import { openEventStream, type TStreamMessage } from '@/store/eventStream'
import { startVideoPlayback, VideoSessionHttpError, type TVideoConnectionState } from '@/store/liveVideo'
import { liveTriviaModule } from '@/store/liveTrivia/module'
import type {
    TLiveGameErrorCode,
    TLiveGameSnapshot,
    TLiveGameStatus,
    TLiveGameStreamEvent,
    TLiveHostMessage,
} from '@/store/liveTrivia/types'

const liveGameErrorCodes: readonly TLiveGameErrorCode[] = [
    'NOT_PREMIUM',
    'GAME_STARTED',
    'GAME_NOT_FOUND',
    'NOT_JOINED',
    'ELIMINATED',
    'WRONG_QUESTION',
    'CLOSED',
    'ALREADY_ANSWERED',
    'INVALID_CHOICE',
]

const isLiveGameErrorCode = (value: unknown): value is TLiveGameErrorCode =>
    typeof value === 'string' && liveGameErrorCodes.some(code => code === value)

/** The axios failure behind an error: the error itself, or the one `callApi` wrapped as its `cause`. */
const findAxiosError = (error: unknown) => {
    if (isAxiosError(error)) {
        return error
    }
    const cause = error instanceof Error && 'cause' in error ? error.cause : null
    return isAxiosError(cause) ? cause : null
}

/**
 * Reads the contract's machine-readable error `code` from a failed live-games request.
 * nest answers `{ statusCode, error, code, message }`; `callApi` rethrows that as `new Error(error)` with the
 * axios error as its `cause` (or rethrows the axios error itself when there's no `error`), so the body is
 * read from there.
 */
const getLiveGameErrorCode = (error: unknown): TLiveGameErrorCode | 'UNKNOWN' => {
    const responseBody: unknown = findAxiosError(error)?.response?.data
    const errorCode = responseBody && typeof responseBody === 'object' && 'code' in responseBody
        ? responseBody.code
        : null
    if (isLiveGameErrorCode(errorCode)) {
        return errorCode
    }
    // Uncoded failures (network, 5xx, contract drift) are worth knowing about; coded ones are expected.
    Sentry.captureException(error)
    return 'UNKNOWN'
}

const updateServerClockOffset = (serverTime: string) => {
    const serverTimeMs = Date.parse(serverTime)
    if (Number.isNaN(serverTimeMs)) {
        return
    }
    liveTriviaModule.state.serverClockOffsetMs = serverTimeMs - Date.now()
}

const statusProgressOrder: Record<TLiveGameStatus, number> = {
    scheduled: 0,
    question_open: 1,
    question_locked: 2,
    question_revealed: 3,
    ended: 4,
}

/** Orders snapshots of one game by how far the game has progressed, to drop late-arriving stale ones. */
const getGameProgressRank = (game: Pick<TLiveGameSnapshot, 'status' | 'currentIndex'>) => {
    if (game.status === 'ended') {
        return Number.POSITIVE_INFINITY
    }
    return (game.currentIndex ?? -1) * 10 + statusProgressOrder[game.status]
}

/**
 * The server doesn't push per-player results; each client works out its own elimination at reveal:
 * a joined, alive player who didn't pick the correct choice is out — unless the everyone-out rule
 * revived the round. Safe to run repeatedly for the same reveal.
 */
const applyRevealToPlayer = () => {
    const { game, player } = liveTriviaModule.state
    const reveal = game?.reveal
    if (!reveal || !player?.joined || !player.alive || reveal.revived) {
        return
    }
    if (player.answers[reveal.index] !== reveal.correctIndex) {
        player.alive = false
        liveTriviaModule.state.eliminatedAtQuestionIndex = reveal.index
    }
}

/** How long to wait before asking for the host's video again when it isn't available */
const VIDEO_RETRY_DELAY_MS = 5000
/** A session can come up while the host isn't broadcasting; with no video this long, start over */
const VIDEO_STALL_TIMEOUT_MS = 5000

type TVideoPlayback = Awaited<ReturnType<typeof startVideoPlayback>>

let activeVideoPlayback: TVideoPlayback | null = null
/** The playback URL the video player is following, from the first attempt until `stopVideoPlayback` */
let followedPlaybackUrl: string | null = null
/** Bumped on every attempt and on stop, so callbacks and results from an older attempt are ignored */
let videoAttemptGeneration = 0
let videoRetryTimeoutId: ReturnType<typeof setTimeout> | null = null
let videoStallTimeoutId: ReturnType<typeof setTimeout> | null = null
let hasReportedVideoError = false

const clearVideoTimers = () => {
    if (videoRetryTimeoutId) {
        clearTimeout(videoRetryTimeoutId)
        videoRetryTimeoutId = null
    }
    if (videoStallTimeoutId) {
        clearTimeout(videoStallTimeoutId)
        videoStallTimeoutId = null
    }
}

/** Ends the current video session; its DELETE is housekeeping, so a failure is only reported */
const closeActiveVideoPlayback = () => {
    const playback = activeVideoPlayback
    activeVideoPlayback = null
    liveTriviaModule.state.videoStream = null
    playback?.stop().catch((error: unknown) => {
        Sentry.captureException(error)
    })
}

/** Drops the current session and, unless the game is over, tries again shortly */
const retryVideoPlaybackLater = () => {
    const state = liveTriviaModule.state
    videoAttemptGeneration++
    clearVideoTimers()
    closeActiveVideoPlayback()
    if (state.game?.status === 'ended') {
        state.videoPlaybackStatus = 'ended'
        return
    }
    state.videoPlaybackStatus = 'waitingForHost'
    const generation = videoAttemptGeneration
    videoRetryTimeoutId = setTimeout(() => {
        if (generation === videoAttemptGeneration) {
            // connectVideoPlayback handles its own failures, so it can't reject
            void connectVideoPlayback()
        }
    }, VIDEO_RETRY_DELAY_MS)
}

const watchForVideoStall = (generation: number) => {
    if (videoStallTimeoutId) {
        clearTimeout(videoStallTimeoutId)
    }
    videoStallTimeoutId = setTimeout(() => {
        if (generation === videoAttemptGeneration) {
            retryVideoPlaybackLater()
        }
    }, VIDEO_STALL_TIMEOUT_MS)
}

const handleVideoFlowChange = ({ generation, isVideoFlowing }: { generation: number; isVideoFlowing: boolean }) => {
    if (generation !== videoAttemptGeneration) {
        return
    }
    if (isVideoFlowing) {
        if (videoStallTimeoutId) {
            clearTimeout(videoStallTimeoutId)
            videoStallTimeoutId = null
        }
        liveTriviaModule.state.videoPlaybackStatus = 'playing'
        return
    }
    liveTriviaModule.state.videoPlaybackStatus = 'waitingForHost'
    watchForVideoStall(generation)
}

const handleVideoConnectionStateChange = ({ generation, connectionState }: {
    generation: number
    connectionState: TVideoConnectionState
}) => {
    if (generation !== videoAttemptGeneration) {
        return
    }
    if (connectionState === 'failed' || connectionState === 'closed') {
        retryVideoPlaybackLater()
    } else if (connectionState === 'disconnected') {
        // Often recovers by itself; if video doesn't come back, the stall watch starts over
        handleVideoFlowChange({ generation, isVideoFlowing: false })
    }
}

/**
 * One attempt to play the host's video. Any failure means "not available right now" (Cloudflare refuses
 * playback while the host isn't broadcasting), so it retries every few seconds rather than giving up.
 */
const connectVideoPlayback = async () => {
    const state = liveTriviaModule.state
    const playbackUrl = followedPlaybackUrl
    if (!playbackUrl) {
        return
    }
    videoAttemptGeneration++
    const generation = videoAttemptGeneration
    if (state.videoPlaybackStatus === 'off') {
        state.videoPlaybackStatus = 'connecting'
    }

    try {
        const playback = await startVideoPlayback({
            playbackUrl,
            onConnectionStateChange: connectionState => {
                handleVideoConnectionStateChange({ generation, connectionState })
            },
            onVideoFlowChange: isVideoFlowing => {
                handleVideoFlowChange({ generation, isVideoFlowing })
            },
        })
        if (generation !== videoAttemptGeneration) {
            playback.stop().catch((error: unknown) => {
                Sentry.captureException(error)
            })
            return
        }
        activeVideoPlayback = playback
        state.videoStream = markRaw(playback.mediaStream)
        if (state.videoPlaybackStatus !== 'playing') {
            watchForVideoStall(generation)
        }
    } catch (error) {
        if (generation !== videoAttemptGeneration) {
            return
        }
        // A refusal is expected while the host is offline; anything else is worth one report per video
        if (!(error instanceof VideoSessionHttpError) && !hasReportedVideoError) {
            hasReportedVideoError = true
            Sentry.captureException(error)
        }
        retryVideoPlaybackLater()
    }
}

const stopVideoPlayback = () => {
    videoAttemptGeneration++
    clearVideoTimers()
    closeActiveVideoPlayback()
    followedPlaybackUrl = null
    hasReportedVideoError = false
    liveTriviaModule.state.videoPlaybackStatus = 'off'
}

/** Starts following the game's video once it has one. Video never blocks the game; it's best effort. */
const syncVideoPlayback = () => {
    const game = liveTriviaModule.state.game
    const playbackUrl = game?.video?.playbackUrl ?? null
    if (!playbackUrl || playbackUrl === followedPlaybackUrl) {
        return
    }
    stopVideoPlayback()
    // A finished game's video input is already gone
    if (game?.status === 'ended') {
        return
    }
    followedPlaybackUrl = playbackUrl
    // connectVideoPlayback handles its own failures, so it can't reject
    void connectVideoPlayback()
}

/** Keeps the side panel's feed long enough to scroll back through without growing all game */
const MAX_HOST_MESSAGES = 50

const recordHostMessage = (hostMessage: TLiveHostMessage | null) => {
    const hostMessages = liveTriviaModule.state.hostMessages
    if (!hostMessage || hostMessages.some(knownMessage => knownMessage.at === hostMessage.at)) {
        return
    }
    hostMessages.push(hostMessage)
    hostMessages.sort((a, b) => Date.parse(a.at) - Date.parse(b.at))
    hostMessages.splice(0, Math.max(0, hostMessages.length - MAX_HOST_MESSAGES))
}

/**
 * Applies a full snapshot from `/state`, `/join`, or the stream's `snapshot` event.
 * Snapshots can land out of order (a slow `/state` racing the stream), so an older snapshot of the same
 * game never rewinds the game, and player facts only move forward: joined/winner never unset,
 * alive never comes back, accepted answers are kept.
 */
const applyGameSnapshot = (snapshot: TLiveGameSnapshot) => {
    const state = liveTriviaModule.state
    updateServerClockOffset(snapshot.serverTime)

    const { me: incomingPlayer, ...incomingGame } = snapshot
    const currentGame = state.game
    const isSameGame = currentGame?.id === incomingGame.id
    const isStale = isSameGame && getGameProgressRank(incomingGame) < getGameProgressRank(currentGame)
    // Video is only ever added to a game, so an older snapshot without it doesn't take it away
    const knownVideo = isSameGame ? currentGame.video : null
    const video = incomingGame.video ?? knownVideo ?? null
    if (!isStale) {
        state.game = { ...incomingGame, video }
    } else if (currentGame) {
        currentGame.video = video
    }

    if (incomingPlayer) {
        const knownPlayer = isSameGame ? state.player : null
        // Only a joined player can be knocked out: before joining, nest reports `alive: false`,
        // and that must not stick once they join.
        const wasKnockedOut = !!knownPlayer?.joined && !knownPlayer.alive
        state.player = {
            joined: incomingPlayer.joined || !!knownPlayer?.joined,
            alive: incomingPlayer.alive && !wasKnockedOut,
            answers: { ...knownPlayer?.answers, ...incomingPlayer.answers },
            isWinner: incomingPlayer.isWinner || !!knownPlayer?.isWinner,
            isPremium: incomingPlayer.isPremium,
        }
    } else if (!isSameGame) {
        state.player = null
    }
    if (!isSameGame) {
        state.eliminatedAtQuestionIndex = null
        state.hostMessages = []
    }
    recordHostMessage(incomingGame.hostMessage)

    applyRevealToPlayer()
    syncVideoPlayback()
}

const fetchGameState = async (gameId: string) => {
    const snapshot: TLiveGameSnapshot = await callApi({
        method: 'GET',
        url: `/live-games/${gameId}/state`,
    })
    applyGameSnapshot(snapshot)
}

/** Background resync: a failure here leaves slightly stale data on screen, which the stream will correct. */
const refreshGameState = async (gameId: string) => {
    try {
        await fetchGameState(gameId)
    } catch (error) {
        Sentry.captureException(error)
    }
}

const fetchUpcomingGame = async (examGuid: string) => {
    // The endpoint answers `null` when nothing is scheduled; an empty body arrives as ''.
    const upcomingGame: TLiveGameSnapshot | null | '' = await callApi({
        method: 'GET',
        url: '/live-games/upcoming',
        params: { examGuid },
    })
    const state = liveTriviaModule.state
    state.upcomingGameExamGuid = examGuid
    state.upcomingGame = upcomingGame || null
    if (upcomingGame) {
        updateServerClockOffset(upcomingGame.serverTime)
    }
    return state.upcomingGame
}

const streamEventNames: readonly TLiveGameStreamEvent['eventName'][] = [
    'snapshot',
    'lobby',
    'question_open',
    'question_locked',
    'answer_reveal',
    'host_message',
    'game_end',
    'video_ready',
]

const parseStreamEvent = (message: TStreamMessage): TLiveGameStreamEvent | null => {
    if (!streamEventNames.some(eventName => eventName === message.eventName)) {
        return null
    }
    const payload: unknown = JSON.parse(message.data)
    // The wire format is the contract; there's no runtime schema to narrow against.
    return { eventName: message.eventName, payload } as TLiveGameStreamEvent
}

const applyStreamEvent = (event: TLiveGameStreamEvent) => {
    const state = liveTriviaModule.state
    if (event.eventName === 'snapshot') {
        applyGameSnapshot(event.payload)
        return
    }

    // The stream always opens with a snapshot, so a missing game means the event belongs to a game we left.
    const game = state.game
    if (!game) {
        return
    }

    switch (event.eventName) {
        case 'lobby':
            game.playerCount = event.payload.playerCount
            break
        case 'question_open':
            if (game.currentIndex !== null && event.payload.index < game.currentIndex) {
                return
            }
            game.status = 'question_open'
            game.currentIndex = event.payload.index
            game.currentQuestion = event.payload.question
            game.opensAt = event.payload.opensAt
            game.closesAt = event.payload.closesAt
            game.aliveCount = event.payload.aliveCount
            game.reveal = null
            state.answerErrorCode = null
            break
        case 'question_locked':
            if (event.payload.index !== game.currentIndex || game.status !== 'question_open') {
                return
            }
            game.status = 'question_locked'
            break
        case 'answer_reveal':
            if (game.currentIndex !== null && event.payload.index < game.currentIndex) {
                return
            }
            game.status = 'question_revealed'
            game.currentIndex = event.payload.index
            game.reveal = event.payload
            game.aliveCount = event.payload.aliveCount
            applyRevealToPlayer()
            break
        case 'host_message':
            game.hostMessage = event.payload
            recordHostMessage(event.payload)
            break
        case 'game_end': {
            game.status = 'ended'
            game.winner = event.payload.winner
            game.aliveCount = event.payload.aliveCount
            // Only `/state` says whether the winner is this user.
            refreshGameState(game.id)
            break
        }
        case 'video_ready':
            game.video = event.payload
            syncVideoPlayback()
            break
    }
}

let activeGameStream: ReturnType<typeof openEventStream> | null = null

const handleStreamMessage = (message: TStreamMessage) => {
    const event = parseStreamEvent(message)
    if (event) {
        applyStreamEvent(event)
    }
}

const connectToGameEvents = (gameId: string) => {
    activeGameStream?.close()
    activeGameStream = openEventStream({
        buildRequest: () => buildSignedNestStreamRequest(`/live-games/${gameId}/events`),
        onMessage: handleStreamMessage,
        onOpen: ({ isReconnect }) => {
            if (!isReconnect) {
                return
            }
            // The stream's snapshot omits the player; `/state` fills in anything missed while disconnected.
            refreshGameState(gameId)
        },
        onStatusChange: status => {
            liveTriviaModule.state.connectionStatus = status
        },
        onFatalError: error => {
            Sentry.captureException(error)
        },
    })
}

const leaveGame = () => {
    activeGameStream?.close()
    activeGameStream = null
    stopVideoPlayback()
    const state = liveTriviaModule.state
    state.game = null
    state.hostMessages = []
    state.player = null
    state.eliminatedAtQuestionIndex = null
    state.connectionStatus = 'idle'
    state.isLoadingGame = false
    state.loadGameErrorCode = null
    state.isJoining = false
    state.joinErrorCode = null
    state.pendingAnswer = null
    state.answerErrorCode = null
}

/** Loads a game for the live view and, unless it's already over, follows its event stream. */
const enterGame = async (gameId: string) => {
    leaveGame()
    const state = liveTriviaModule.state
    state.isLoadingGame = true
    try {
        await fetchGameState(gameId)
    } catch (error) {
        state.loadGameErrorCode = getLiveGameErrorCode(error)
        return
    } finally {
        state.isLoadingGame = false
    }

    if (state.game?.status !== 'ended') {
        connectToGameEvents(gameId)
    }
}

const joinGame = async () => {
    const state = liveTriviaModule.state
    const game = state.game
    if (!game) {
        throw new Error('joinGame: no game loaded')
    }

    state.isJoining = true
    state.joinErrorCode = null
    try {
        // An empty JSON object rather than no body: Fastify rejects an empty body sent as application/json.
        const snapshot: TLiveGameSnapshot = await callApi({
            method: 'POST',
            url: `/live-games/${game.id}/join`,
            data: {},
        })
        applyGameSnapshot(snapshot)
    } catch (error) {
        state.joinErrorCode = getLiveGameErrorCode(error)
        if (state.joinErrorCode === 'GAME_STARTED') {
            await refreshGameState(game.id)
        }
    } finally {
        state.isJoining = false
    }
}

/** Locks in the player's choice for the open question. One tap, no changing it afterwards. */
const submitAnswer = async (choiceIndex: number) => {
    const state = liveTriviaModule.state
    const { game, player } = state
    const questionIndex = game?.currentIndex
    if (!game || questionIndex === null || questionIndex === undefined) {
        throw new Error('submitAnswer: no open question')
    }
    if (!player || state.pendingAnswer || questionIndex in player.answers) {
        return
    }

    state.pendingAnswer = { index: questionIndex, choiceIndex }
    state.answerErrorCode = null
    try {
        await callApi({
            method: 'POST',
            url: `/live-games/${game.id}/answers`,
            data: { index: questionIndex, choiceIndex },
        })
        player.answers[questionIndex] = choiceIndex
    } catch (error) {
        state.answerErrorCode = getLiveGameErrorCode(error)
        // The server knows better than we do what was recorded (or whether we're still in); resync.
        if (state.answerErrorCode !== 'UNKNOWN' && state.answerErrorCode !== 'INVALID_CHOICE') {
            await refreshGameState(game.id)
        }
    } finally {
        state.pendingAnswer = null
    }
}

export default {
    fetchUpcomingGame,
    enterGame,
    leaveGame,
    joinGame,
    submitAnswer,
    applyStreamEvent,
    handleStreamMessage,
}
